/**
 * MIRROR von `src/lib/bv-auto-pricing.ts` (Backend).
 *
 * Backend ist Source of Truth. Bei Backend-Änderungen 1:1 hierher übernehmen.
 * Drift-Schutz: `bv-lib-drift.test.ts`.
 *
 * BV-Plan §5.3.
 */

import {
  MODUL_TEMPLATES,
  PRICING_RUNDUNGSSCHRITT_DEFAULT,
  type BvModulId,
  type PricingPerModul,
} from './bv-modul-templates';

export interface PricingShape {
  haustechnik: PricingPerModul['haustechnik'];
  innenreinigung: PricingPerModul['innenreinigung'];
  aussenreinigung: PricingPerModul['aussenreinigung'];
  gruenflaechenpflege: PricingPerModul['gruenflaechenpflege'];
  winterdienst: PricingPerModul['winterdienst'];
  bereitschaftsdienst: PricingPerModul['bereitschaftsdienst'];
  sonderarbeiten: PricingPerModul['sonderarbeiten'];
  rundungsschritt: number;
}

export const PRICING_DEFAULT: PricingShape = {
  haustechnik: MODUL_TEMPLATES.haustechnik.pricing,
  innenreinigung: MODUL_TEMPLATES.innenreinigung.pricing,
  aussenreinigung: MODUL_TEMPLATES.aussenreinigung.pricing,
  gruenflaechenpflege: MODUL_TEMPLATES.gruenflaechenpflege.pricing,
  winterdienst: MODUL_TEMPLATES.winterdienst.pricing,
  bereitschaftsdienst: MODUL_TEMPLATES.bereitschaftsdienst.pricing,
  sonderarbeiten: MODUL_TEMPLATES.sonderarbeiten.pricing,
  rundungsschritt: PRICING_RUNDUNGSSCHRITT_DEFAULT,
};

export type PricingModulOverrideKey = `modul.${BvModulId}`;
export type PricingGlobalOverrideKey = `global.${'rundungsschritt'}`;
export type PricingOverrideKey = PricingModulOverrideKey | PricingGlobalOverrideKey;

export function modulOverrideKey(modulId: BvModulId): PricingModulOverrideKey {
  return `modul.${modulId}`;
}

export type Klasse = 'klein' | 'mittel' | 'gross';
export type ReinigungProWocheKey = '0.5' | '1' | '2' | '3' | '4' | '5' | '6' | '7';

export interface ObjektPricingInput {
  anzahlEingaenge: number | null;
  hausGroesseKlasse: Klasse | null;
  anzahlEinheiten: number | null;
  flurgroesseKlasse: Klasse | null;
  reinigungProWoche: ReinigungProWocheKey | null;
  aussenflaecheQm: number | null;
  gruenflaecheQm: number | null;
  winterdienstflaecheQm: number | null;
}

export interface PricingVorschlag {
  modulId: BvModulId;
  vorschlagBetrag: number;
  herleitung: string;
  kannBerechnetWerden: boolean;
  fehlendeFelder: string[];
}

function ceilToStep(value: number, step: number): number {
  if (step <= 0) return value;
  return Math.ceil(value / step) * step;
}

function fmtEur(n: number): string {
  return `${n.toFixed(2).replace('.', ',')} €`;
}

const KLASSE_LABEL: Record<Klasse, string> = {
  klein: 'klein',
  mittel: 'mittel',
  gross: 'groß',
};

function makeNotComputable(
  modulId: BvModulId,
  fehlendeFelder: string[],
  hinweis = 'Felder am Objekt fehlen',
): PricingVorschlag {
  return {
    modulId,
    vorschlagBetrag: 0,
    herleitung: `${hinweis}: ${fehlendeFelder.join(', ')}`,
    kannBerechnetWerden: false,
    fehlendeFelder,
  };
}

const PFLICHT_FELDER: Record<BvModulId, (keyof ObjektPricingInput)[]> = {
  haustechnik: ['anzahlEingaenge', 'hausGroesseKlasse'],
  innenreinigung: ['anzahlEinheiten', 'flurgroesseKlasse', 'reinigungProWoche'],
  aussenreinigung: ['aussenflaecheQm'],
  gruenflaechenpflege: ['gruenflaecheQm'],
  winterdienst: ['winterdienstflaecheQm'],
  bereitschaftsdienst: ['hausGroesseKlasse'],
  sonderarbeiten: [], // immer manuell, nie kann
};

function fehlendeFelderFuer(modulId: BvModulId, objekt: Partial<ObjektPricingInput>): string[] {
  return PFLICHT_FELDER[modulId].filter((f) => {
    const v = objekt[f];
    return v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v));
  });
}

export function kannBerechnetWerden(
  modulId: BvModulId,
  objekt: Partial<ObjektPricingInput>,
): boolean {
  if (modulId === 'sonderarbeiten') return false;
  return fehlendeFelderFuer(modulId, objekt).length === 0;
}

interface HaustechnikInput {
  anzahlEingaenge: number;
  hausGroesseKlasse: Klasse;
}

interface InnenreinigungInput {
  anzahlEinheiten: number;
  flurgroesseKlasse: Klasse;
  reinigungProWoche: ReinigungProWocheKey;
}

function berechneHaustechnik(
  pricing: PricingShape,
  objekt: HaustechnikInput,
): { betrag: number; herleitung: string } {
  const klassePreis = pricing.haustechnik.klassePreise[objekt.hausGroesseKlasse];
  const betrag = klassePreis * objekt.anzahlEingaenge;
  const eingangWort = objekt.anzahlEingaenge === 1 ? 'Eingang' : 'Eingänge';
  const herleitung = `${objekt.anzahlEingaenge} ${eingangWort} × ${fmtEur(klassePreis)} (${KLASSE_LABEL[objekt.hausGroesseKlasse]}) = ${fmtEur(betrag)}/Monat`;
  return { betrag, herleitung };
}

function berechneInnenreinigung(
  pricing: PricingShape,
  objekt: InnenreinigungInput,
): { betrag: number; herleitung: string } {
  const klassePreis = pricing.innenreinigung.klassePreise[objekt.flurgroesseKlasse];
  const faktor = pricing.innenreinigung.frequenzFaktor[objekt.reinigungProWoche];
  const betrag = objekt.anzahlEinheiten * klassePreis * faktor;
  const freqLabel =
    objekt.reinigungProWoche === '0.5'
      ? '0,5×/Woche (alle 14 Tage)'
      : `${objekt.reinigungProWoche}×/Woche`;
  const herleitung = `${objekt.anzahlEinheiten} Einheiten × ${fmtEur(klassePreis)} (${KLASSE_LABEL[objekt.flurgroesseKlasse]}) × ${faktor.toString().replace('.', ',')} (${freqLabel}) = ${fmtEur(betrag)}/Monat`;
  return { betrag, herleitung };
}

function berechneProQm(
  euroProQm: number,
  flaecheQm: number,
  modulName: string,
): { betrag: number; herleitung: string } {
  const betrag = flaecheQm * euroProQm;
  const herleitung = `${flaecheQm.toString().replace('.', ',')} m² × ${fmtEur(euroProQm)}/m² (${modulName}) = ${fmtEur(betrag)}/Monat`;
  return { betrag, herleitung };
}

function berechneBereitschaft(
  pricing: PricingShape,
  klasse: Klasse,
): { betrag: number; herleitung: string } {
  const betrag = pricing.bereitschaftsdienst[klasse];
  const herleitung = `Bereitschafts-Pauschale (${KLASSE_LABEL[klasse]}) = ${fmtEur(betrag)}/Monat`;
  return { betrag, herleitung };
}

export function berechneVorschlagFuerModul(
  modulId: BvModulId,
  objekt: Partial<ObjektPricingInput>,
  pricing: PricingShape = PRICING_DEFAULT,
): PricingVorschlag {
  if (modulId === 'sonderarbeiten') {
    return makeNotComputable(
      modulId,
      [],
      'Sonderarbeiten werden nach Aufwand abgerechnet — kein Auto-Vorschlag',
    );
  }

  const fehlend = fehlendeFelderFuer(modulId, objekt);
  if (fehlend.length > 0) {
    return makeNotComputable(modulId, fehlend);
  }

  let raw: { betrag: number; herleitung: string };
  switch (modulId) {
    case 'haustechnik':
      raw = berechneHaustechnik(pricing, {
        anzahlEingaenge: objekt.anzahlEingaenge as number,
        hausGroesseKlasse: objekt.hausGroesseKlasse as Klasse,
      });
      break;
    case 'innenreinigung':
      raw = berechneInnenreinigung(pricing, {
        anzahlEinheiten: objekt.anzahlEinheiten as number,
        flurgroesseKlasse: objekt.flurgroesseKlasse as Klasse,
        reinigungProWoche: objekt.reinigungProWoche as ReinigungProWocheKey,
      });
      break;
    case 'aussenreinigung':
      raw = berechneProQm(
        pricing.aussenreinigung.euroProQm,
        objekt.aussenflaecheQm as number,
        'Außenreinigung',
      );
      break;
    case 'gruenflaechenpflege':
      raw = berechneProQm(
        pricing.gruenflaechenpflege.euroProQm,
        objekt.gruenflaecheQm as number,
        'Grünflächenpflege',
      );
      break;
    case 'winterdienst':
      raw = berechneProQm(
        pricing.winterdienst.euroProQm,
        objekt.winterdienstflaecheQm as number,
        'Winterdienst',
      );
      break;
    case 'bereitschaftsdienst':
      raw = berechneBereitschaft(pricing, objekt.hausGroesseKlasse as Klasse);
      break;
  }

  const gerundet = ceilToStep(raw.betrag, pricing.rundungsschritt);
  const herleitung =
    gerundet === raw.betrag
      ? raw.herleitung
      : `${raw.herleitung} → gerundet auf ${fmtEur(gerundet)}`;

  return {
    modulId,
    vorschlagBetrag: gerundet,
    herleitung,
    kannBerechnetWerden: true,
    fehlendeFelder: [],
  };
}
